import { Button, Popconfirm, Table, Tag, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ApiKey } from '@mp-pro/shared/interfaces';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface Props {
    keys: ApiKey[];
    loading: boolean;
    onRevoke: (keyId: string) => void;
    revoking: boolean;
}

export default function ApiKeyList({ keys, loading, onRevoke, revoking }: Props) {
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Key',
            dataIndex: 'keyPrefix',
            key: 'keyPrefix',
            render: (prefix: string) => (
                <code>{prefix}...</code>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => (
                <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm')}>
                    {dayjs(date).fromNow()}
                </Tooltip>
            ),
        },
        {
            title: 'Last Used',
            dataIndex: 'lastUsedAt',
            key: 'lastUsedAt',
            render: (date: string | null) => date ? (
                <Tooltip title={dayjs(date).format('YYYY-MM-DD HH:mm')}>
                    {dayjs(date).fromNow()}
                </Tooltip>
            ) : <span style={{ color: '#999' }}>Never</span>,
        },
        {
            title: 'Expires',
            dataIndex: 'expiresAt',
            key: 'expiresAt',
            render: (date: string | null) => {
                if (!date) return <span style={{ color: '#999' }}>Never</span>;
                const d = dayjs(date);
                const expired = d.isBefore(dayjs());
                return (
                    <Tooltip title={d.format('YYYY-MM-DD HH:mm')}>
                        <span style={expired ? { color: '#fa8c16' } : undefined}>{d.fromNow()}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Status',
            key: 'status',
            render: (_: unknown, record: ApiKey) => {
                if (record.revokedAt) return <Tag color="red">Revoked</Tag>;
                if (record.expiresAt && dayjs(record.expiresAt).isBefore(dayjs())) return <Tag color="orange">Expired</Tag>;
                return <Tag color="green">Active</Tag>;
            },
        },
        {
            title: '',
            key: 'actions',
            width: 60,
            render: (_: unknown, record: ApiKey) => {
                if (record.revokedAt) return null;
                return (
                    <Popconfirm
                        title="Revoke this API key?"
                        description="This action cannot be undone. Any requests using this key will be rejected."
                        onConfirm={() => onRevoke(record.id)}
                        okText="Revoke"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            loading={revoking}
                        />
                    </Popconfirm>
                );
            },
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={keys}
            rowKey="id"
            loading={loading}
            pagination={false}
            size="small"
        />
    );
}
